import React, { useContext, useLayoutEffect, useState } from 'react'
import { useFormik } from 'formik'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { useHistory, useLocation } from 'react-router'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import * as S from './styles'
import AccordionCardNew from 'presentation/shared/components/AccordionCardNew'
import { ReactComponent as ArrowIcon } from 'presentation/assets/icons/close-arrow.svg'
import { ReactComponent as CheckCircle } from 'presentation/assets/icons/check-circle.svg'
import { ReactComponent as AlertCircle } from 'presentation/assets/icons/alert-circle.svg'
import MultimediaFileInputNew from 'presentation/shared/components/MultimediaFileInputNew'
import {
  AmbulatoryAuthorizationDashboardContext,
  DocumentFile
} from 'main/factories/pages/doctor/ambulatory-authorization/ambulatory-authorization-dashboard-factory'
import { documentList } from './documentList'
import { toast } from 'react-toastify'
import { useServices } from 'presentation/hooks/use-services'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { AmbulatoryDoctorDocument } from 'common/enum/ambulatory-doctor-document'

type LocationProps = {
  isEditing?: boolean
}

const AmbulatoryDocumentsLayout = WithLoading(
  ({ setIsLoading }: WithLoadingProps) => {
    const history = useHistory()
    const stateLocation = useLocation()?.state as LocationProps
    const patientDocumentService = useServices().patientDocument
    const [informationAccordionOpen, setInformationAccordionOpen] = useState('')
    const { state, dispatch } = useContext<any>(
      AmbulatoryAuthorizationDashboardContext
    )
    const [files, setFiles] = useState<DocumentFile[]>()
    const [error, setError] = useState<boolean>(false)

    if (!state?.patient?.cpf) {
      history.push('/pedidos-autorizacao-ambulatorial/solicitacao')
    }

    const formik = useFormik({
      initialValues: {},
      onSubmit: () => {
        if (documentsIsValid()) {
          dispatch({
            type: 'DOCUMENTS',
            payload: files
          })
          stateLocation?.isEditing
            ? history.push('resumo#docs')
            : history.push('resumo')
        } else {
          setError(true)
          toast.error('Adicione documentos obrigatórios')
        }
      }
    })

    const documentsIsValid = () => {
      const requiredDocsLength = documentList.filter(
        (document) => document.required
      )?.length
      const selectedRequiredDocsLength = files?.filter(
        (doc) => doc.required
      )?.length

      return requiredDocsLength === selectedRequiredDocsLength
    }

    const handleChangeInformationAccordion = (
      label: string,
      isExpanded: boolean
    ) => {
      setInformationAccordionOpen(!isExpanded ? '' : label)
    }

    const documents = () => {
      const mappedDocs = files?.map((document: DocumentFile) => document || [])

      return documentList.map((docType: DocumentFile) => {
        const doc = mappedDocs?.find(
          (doc: DocumentFile) => doc.type === docType.type
        )

        return {
          ...docType,
          document_id: doc?.document_id,
          filename: doc?.filename,
          file: doc?.file,
          status: doc?.status
        }
      })
    }

    const handleFileChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      document: DocumentFile
    ) => {
      const file = e.target.files?.[0]

      if (file) {
        const currentDocument = {
          ...document,
          status: 'ATTACHED',
          file: file
        }

        const docsUpdatedList = files?.filter(
          (doc: DocumentFile) => doc.type !== currentDocument.type
        )
        docsUpdatedList?.push(currentDocument)

        setFiles(docsUpdatedList)
      }
    }

    const handleFileDelete = (document: DocumentFile) => {
      const docsUpdatedList = files?.filter(
        (doc: DocumentFile) => doc.type !== document.type
      )

      setFiles(docsUpdatedList)
    }

    const getPatientDocuments = async () => {
      try {
        setIsLoading(true)
        const contextDocuments = state.documents

        const patientDocumentsResponse =
          await patientDocumentService.getPatientDocuments({
            patient_id: state.patient.patient_id
          })

        // Filter pre-uploaded docs available to merge (id and health insurance card)
        const mapAvailablePreUploadedDocs = patientDocumentsResponse?.filter(
          (document) =>
            document?.document_type ===
              AmbulatoryDoctorDocument['Carteira de identidade'] ||
            document?.document_type ===
              AmbulatoryDoctorDocument['Carteira do convênio']
        )

        // Merge contextDocuments and mapAvailablePreUploadedDocs with documentList (default document list)
        const mergeDocList = documentList.map((docItem: DocumentFile) => {
          const docOnContext: DocumentFile = contextDocuments?.find(
            (docContextItem: DocumentFile) =>
              docContextItem.type === docItem.type
          )

          const docPreUploaded: any = mapAvailablePreUploadedDocs?.find(
            (docPreUploadedItem) =>
              docPreUploadedItem.document_type === docItem.type
          )

          // Document on Context (prioritized)
          if (docOnContext) {
            return {
              ...docItem,
              document_id: docOnContext?.document_id,
              filename: docOnContext?.filename,
              file: docOnContext?.file,
              status: docOnContext?.status
              // test: 'docOnContext'
            }
          }

          // Document Pre Uploaded (getPatientDocuments)
          return {
            ...docItem,
            document_id: docPreUploaded?.document_id,
            filename: docPreUploaded?.document_name,
            file: docPreUploaded?.file,
            status: docPreUploaded?.document_id ? 'ATTACHED' : undefined
          }
        })

        // Last filtered doc list
        const filteredDocList = mergeDocList.filter(
          (doc: DocumentFile) => doc?.document_id || doc?.file?.name
        )

        setFiles(filteredDocList)
      } catch (error: any) {
        toast.error(error?.message)
      } finally {
        setIsLoading(false)
      }
    }

    useLayoutEffect(() => {
      getPatientDocuments()
    }, [])

    return (
      <>
        <HeaderNew
          actualPageOnClick={() =>
            stateLocation?.isEditing ? history.push('resumo') : history.goBack()
          }
          actualPageTitle="Solicitar pedido ambulatorial"
        />

        <ContainerNew
          title="Documentos"
          titleColor="neutral800"
          titleSize="large"
          titleWeight="bold"
          primaryButton={
            <ButtonNew fullWidth size="large" onClick={formik.submitForm}>
              {stateLocation?.isEditing ? 'Salvar' : 'Próximo'}
            </ButtonNew>
          }
        >
          <S.Wrapper>
            {documents()?.map((document, index) => (
              <AccordionCardNew
                key={document.document_id ?? index}
                header={`${document.label} ${document.required ? '*' : ''}`}
                accordionLabel={document.label}
                expandIcon={<ArrowIcon width={16} height={16} />}
                headerIcon={
                  document.status === 'ATTACHED' ? (
                    <CheckCircle />
                  ) : (
                    <AlertCircle />
                  )
                }
                expanded={
                  error && document.required
                    ? true
                    : informationAccordionOpen === document.label
                }
                handleChange={handleChangeInformationAccordion}
                padding="16px"
                border="neutral100"
                shadow="normal"
              >
                <MultimediaFileInputNew
                  name={`file-input-${document.type}`}
                  onChange={(e) => handleFileChange(e, document)}
                  onDelete={() => handleFileDelete(document)}
                  fileName={document.file?.name ?? document.filename ?? ''}
                  maxFileSizeMB={document.maxFileSizeInMB}
                  errorMessage={
                    error && document.required
                      ? 'Documento obrigatório'
                      : undefined
                  }
                  // text="Insira o arquivo"
                  // showImagePreview
                  // disabled
                />
              </AccordionCardNew>
            ))}
          </S.Wrapper>
        </ContainerNew>
      </>
    )
  }
)

export default AmbulatoryDocumentsLayout
